import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// Import hazard symbol images
import hazardSymbol1 from "../../../utils/hazard-symbols/1.avif";
import hazardSymbol2 from "../../../utils/hazard-symbols/2.avif";
import hazardSymbol3 from "../../../utils/hazard-symbols/3.avif";
import hazardSymbol4 from "../../../utils/hazard-symbols/4.avif";
import hazardSymbol5 from "../../../utils/hazard-symbols/5.avif";
import hazardSymbol6 from "../../../utils/hazard-symbols/6.avif";
import hazardSymbol7 from "../../../utils/hazard-symbols/7.avif";
import hazardSymbol8 from "../../../utils/hazard-symbols/8.avif";

const hazardSymbols = [
  {
    id: "explosive",
    name: "Eksplosiv",
    description:
      "Kan eksplodere ved varme, støt, friksjon eller kontakt med andre stoffer",
    category: "physical",
    image: hazardSymbol1,
  },
  {
    id: "flammable",
    name: "Brannfarlig",
    description:
      "Kan antenne ved kontakt med flamme, gnist, varme eller andre tenning",
    category: "physical",
    image: hazardSymbol2,
  },
  {
    id: "oxidizing",
    name: "Oksiderende",
    description:
      "Kan forårsake eller intensivere brann ved kontakt med brennbare materialer",
    category: "physical",
    image: hazardSymbol3,
  },
  {
    id: "corrosive",
    name: "Etsende",
    description: "Kan forårsake alvorlige brannskader på hud og øyne",
    category: "health",
    image: hazardSymbol4,
  },
  {
    id: "toxic",
    name: "Giftig",
    description:
      "Kan forårsake alvorlig helseskade eller død ved inntak, innånding eller hudkontakt",
    category: "health",
    image: hazardSymbol5,
  },
  {
    id: "harmful",
    name: "Skadelig",
    description:
      "Kan forårsake helseskade ved inntak, innånding eller hudkontakt",
    category: "health",
    image: hazardSymbol6,
  },
  {
    id: "irritant",
    name: "Irriterende",
    description: "Kan forårsake hudirritasjon eller øyeskade",
    category: "health",
    image: hazardSymbol7,
  },
  {
    id: "environmental",
    name: "Miljøfarlig",
    description: "Kan forårsake skade på vannmiljø eller andre miljøer",
    category: "environmental",
    image: hazardSymbol8,
  },
];

export const HazardSymbolsSelector = ({ value = [], onChange, label }) => {
  const { t } = useTranslation();
  const [selectedSymbols, setSelectedSymbols] = useState(value);

  useEffect(() => {
    setSelectedSymbols(value);
  }, [value]);

  const handleSymbolToggle = (symbolId) => {
    const newSelection = selectedSymbols.includes(symbolId)
      ? selectedSymbols.filter((id) => id !== symbolId)
      : [...selectedSymbols, symbolId];

    setSelectedSymbols(newSelection);
    onChange?.(newSelection);
  };

  const categories = [
    { id: "physical", name: "Fysiske farer" },
    { id: "health", name: "Helsefarer" },
    { id: "environmental", name: "Miljøfarer" },
  ];

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">
        {label || t("chemical.inventory.hazard_symbols")}
      </label>

      {categories.map((category) => (
        <div key={category.id} className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900">{category.name}</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {hazardSymbols
              .filter((symbol) => symbol.category === category.id)
              .map((symbol) => (
                <div
                  key={symbol.id}
                  className={`relative border-2 rounded-lg p-3 cursor-pointer transition-colors ${
                    selectedSymbols.includes(symbol.id)
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => handleSymbolToggle(symbol.id)}
                >
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedSymbols.includes(symbol.id)}
                      onChange={() => handleSymbolToggle(symbol.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex justify-center mb-2">
                        <img
                          src={symbol.image}
                          alt={symbol.name}
                          className="w-12 h-12 object-contain"
                        />
                      </div>
                      <p className="text-xs font-medium text-gray-900 text-center">
                        {symbol.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 text-center">
                        {symbol.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
};
